import { html, nothing, type TemplateResult } from "lit";
import { artUrl } from "./art-url";
import { readUrlParams } from "./wire";
import type { GameType, Level, Option } from "./protocol";

// The player's finished games and their record, as GET history?page= sends them (platform/history).

export type Outcome = { kind: "Won" | "Drew" | "Eliminated" | "Conceded" | "TimedOut" | "LeftUncounted" };
export type Tally = { kind: "Win" | "Loss" | "Draw" | "Uncounted" };
export type GameRecord = { won: number; lost: number; drawn: number };
export type Records = { overall: GameRecord; vs_people: GameRecord; vs_ai: GameRecord };
export type Opponent = { name: string; ai_level: Option<Level>; outcome: Outcome };
export type HistoryGame = {
  game_id: string;
  ended_at: number;
  game_type: GameType;
  // The turns it lasted as players count them, a turn each a round (platform/history rounds_of).
  rounds: number;
  placing: number;
  outcome: Outcome;
  tally: Tally;
  deck: string;
  // None for a game recorded before results kept it.
  deck_id: Option<string>;
  commanders: string[];
  opponents: Opponent[];
  replayable: boolean;
};
export type HistoryPage = { records: Records; games: HistoryGame[]; page: number; has_more: boolean };

export type GameHistory = {
  // Null while the first page loads.
  shown: HistoryPage | null;
  error: string | null;
  loadingMore: boolean;
  loadMore: () => void;
  // By the deck's id, or by its name for a game recorded without one.
  featuredCardOf: (deckId: string | null, deck: string) => string | null;
};

const TALLY_WORD: Record<Tally["kind"], string> = { Win: "Won", Loss: "Lost", Draw: "Drew", Uncounted: "Left" };
const HOW: Partial<Record<Outcome["kind"], string>> = {
  Conceded: "Conceded",
  TimedOut: "Ran out of time",
  LeftUncounted: "Before it counted",
};

const recordText = (r: GameRecord): string => `${r.won}–${r.lost}–${r.drawn}`;

// Wins, losses and draws the way a tournament standings page writes them, with the split under it.
export function renderRecord(h: GameHistory): TemplateResult | typeof nothing {
  const records = h.shown?.records;
  if (!records) return nothing;
  return html`
    <div class="record" aria-label="Your record: ${records.overall.won} won, ${records.overall.lost} lost, ${records.overall.drawn} drawn">
      <span class="record-overall">${recordText(records.overall)}</span>
      <span class="record-key">won, lost, drawn</span>
      <span class="record-split"><span>vs people <b>${recordText(records.vs_people)}</b></span><span>vs AI <b>${recordText(records.vs_ai)}</b></span></span>
    </div>
  `;
}

export function renderGameHistory(h: GameHistory): TemplateResult {
  const heading = html`<h3 id="game-history">Games</h3>`;
  if (h.error) return html`${heading}<p class="error">${h.error}</p>`;
  if (h.shown === null) return html`${heading}<p class="none" aria-busy="true">Loading your games…</p>`;
  if (h.shown.games.length === 0) return html`${heading}<p>Your finished games show here, newest first. <a href="./">Play one</a></p>`;
  return html`
    ${heading}
    <table class="room-table history-table" aria-labelledby="game-history">
      <thead><tr><th>Result</th><th>Deck</th><th>Opponents</th><th class="turns">Turns</th><th>Ended</th><th><span class="sr-only">Replay</span></th></tr></thead>
      <tbody>${h.shown.games.map((g) => renderGame(g, frontingCardOf(g, h.featuredCardOf)))}</tbody>
    </table>
    ${h.shown.has_more ? html`<p class="history-more"><button class="secondary" ?disabled=${h.loadingMore} @click=${h.loadMore}>
      ${h.loadingMore ? "Loading…" : "Show older games"}</button></p>` : nothing}
  `;
}

// The card shown most among the games loaded so far, else `fallback`.
export function bannerCardOf(h: GameHistory, fallback: string | null): string | null {
  const shown = new Map<string, number>();
  for (const g of h.shown?.games ?? []) {
    const card = frontingCardOf(g, h.featuredCardOf);
    if (card) shown.set(card, (shown.get(card) ?? 0) + 1);
  }
  return [...shown].sort((a, b) => b[1] - a[1])[0]?.[0] ?? fallback;
}

function frontingCardOf(g: HistoryGame, featuredCardOf: GameHistory["featuredCardOf"]): string | null {
  return g.commanders[0] ?? featuredCardOf(g.deck_id.kind === "Some" ? g.deck_id.values[0] : null, g.deck);
}

function renderGame(g: HistoryGame, fronting: string | null): TemplateResult {
  const how = [HOW[g.outcome.kind], g.opponents.length > 1 ? `${ordinal(g.placing)} of ${g.opponents.length + 1}` : null].filter(Boolean).join(", ");
  return html`
    <tr class="history-game ${g.tally.kind.toLowerCase()}" data-game=${g.game_id}>
      <td class="history-result"><b>${TALLY_WORD[g.tally.kind]}</b>${how ? html`<small>${how}</small>` : nothing}</td>
      <td><div class="history-deck">
        <span class="history-art ${fronting ? "" : "no-art"}" style=${fronting ? `--art: url("${artUrl(fronting)}")` : ""}></span>
        <span><b>${g.deck}</b><small>${g.commanders.length > 0 ? g.commanders.join(" and ") : g.game_type.kind}</small></span>
      </div></td>
      <td class="history-opponents">${g.opponents.map((o) => o.ai_level.kind === "Some"
        ? html`<span class="ai-badge">${o.name}</span>`
        : html`<span class="person">${o.name}</span>`)}</td>
      <td class="turns">${g.rounds}</td>
      <td class="history-ended"><time datetime=${new Date(g.ended_at).toISOString()}>${endedText(g.ended_at)}</time></td>
      <td class="history-replay">${g.replayable ? html`<a href=${gameLink(g.game_id)}>Replay</a>` : nothing}</td>
    </tr>
  `;
}

// The site's /g/<id> opens a game on whichever release it ran on, still signed in as this player.
function gameLink(gameId: string): string {
  const url = new URL(`/g/${encodeURIComponent(gameId)}`, location.origin);
  const { user } = readUrlParams();
  if (user) url.searchParams.set("user", user);
  return url.href;
}

function ordinal(n: number): string {
  const tens = n % 100;
  const suffix = tens >= 11 && tens <= 13 ? "th" : ["th", "st", "nd", "rd"][n % 10] ?? "th";
  return `${n}${suffix}`;
}

function endedText(ms: number): string {
  const ended = new Date(ms);
  const now = new Date();
  const time = ended.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  if (ended.toDateString() === now.toDateString()) return `Today, ${time}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (ended.toDateString() === yesterday.toDateString()) return `Yesterday, ${time}`;
  return ended.toLocaleDateString(undefined, ended.getFullYear() === now.getFullYear()
    ? { day: "numeric", month: "short" }
    : { day: "numeric", month: "short", year: "numeric" });
}
